import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PageLayout from "../../components/layout/PageLayout";
import DataTable from "../../components/ui/DataTable";
import StatusChip from "../../components/ui/StatusChip";
import ActionIconButton from "../../components/ui/ActionIconButton";
import { notify, getErrorMessage } from "../../components/feedback/notify";

const formatarValor = (valor) => {
    return Number(valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
};

const EnderecoFiscal = () => {
    const [enderecos, setEnderecos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [editando, setEditando] = useState(null);
    const [dadosEdicao, setDadosEdicao] = useState({});

    const getEnderecoFiscal = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get/enderecosFiscais`, {
                headers: {
                    Authorization: localStorage.getItem("token")
                }
            });

            if (response.status === 200) {
                setEnderecos(response.data.enderecosFiscais || []);
            }
        } catch (error) {
            console.error("Erro ao buscar endereços fiscais:", error);
            notify.error("Não foi possível carregar os endereços fiscais.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        getEnderecoFiscal();
    }, [getEnderecoFiscal]);

    const iniciarEdicao = (endereco) => {
        setEditando(endereco.idEnderecoFiscal);

        setDadosEdicao({
            ...endereco,
        });
    };

    const cancelarEdicao = () => {
        setEditando(null);
        setDadosEdicao({});
    };

    const alterarCampo = (campo, valor) => {
        setDadosEdicao((prev) => ({
            ...prev,
            [campo]: valor,
        }));
    };

    const salvarEdicao = async () => {
        try {
            setSalvando(true);
            await axios.put(`${process.env.REACT_APP_API_URL}/update/enderecoFiscal/${editando}`, dadosEdicao, {
                headers: {
                    Authorization: localStorage.getItem("token")
                }
            });
            setEditando(null);
            setDadosEdicao({});
            notify.success("Endereço fiscal atualizado.");
            await getEnderecoFiscal();
        } catch (error) {
            console.error("Erro ao salvar endereço:", error);
            notify.error(getErrorMessage(error, "Não foi possível salvar o endereço fiscal."));
        } finally {
            setSalvando(false);
        }
    };

    // Coluna editável: mostra o valor ou um campo de texto quando a linha está em edição.
    const editavel = (campo, label, { width, minWidth = 120, type, format } = {}) => ({
        key: campo,
        label,
        minWidth,
        render: (endereco) =>
            editando === endereco.idEnderecoFiscal ? (
                <TextField
                    size="small"
                    type={type}
                    value={dadosEdicao[campo] ?? ""}
                    onChange={(e) => alterarCampo(campo, e.target.value)}
                    sx={{ width: width || '100%', minWidth: width || minWidth }}
                    slotProps={{ htmlInput: { 'aria-label': label } }}
                />
            ) : format ? (
                format(endereco[campo])
            ) : (
                endereco[campo] || "—"
            ),
    });

    const columns = [
        { key: 'idEnderecoFiscal', label: 'ID', width: 64 },
        editavel('rua', 'Rua', { minWidth: 180 }),
        editavel('numero', 'Número', { width: 90, minWidth: 80 }),
        editavel('bairro', 'Bairro', { minWidth: 140 }),
        editavel('cidade', 'Cidade', { minWidth: 140 }),
        editavel('estado', 'UF', { width: 72, minWidth: 64 }),
        editavel('cep', 'CEP', { minWidth: 110 }),
        editavel('complemento', 'Complemento', { minWidth: 140 }),
        editavel('valor', 'Valor', { width: 120, minWidth: 110, type: 'number', format: formatarValor }),
        { key: 'ativo', label: 'Status', render: (e) => <StatusChip label={e.ativo ? 'Ativo' : 'Inativo'} tone={e.ativo ? 'success' : 'neutral'} /> },
        {
            key: 'acoes',
            label: 'Ações',
            align: 'right',
            width: 110,
            render: (endereco) =>
                editando === endereco.idEnderecoFiscal ? (
                    <Box sx={{ display: 'inline-flex', gap: 0.75 }}>
                        <ActionIconButton title="Salvar" color="success" icon={<CheckIcon fontSize="small" />} onClick={salvarEdicao} disabled={salvando} />
                        <ActionIconButton title="Cancelar" icon={<CloseIcon fontSize="small" />} onClick={cancelarEdicao} disabled={salvando} />
                    </Box>
                ) : (
                    <ActionIconButton
                        title="Editar"
                        icon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => iniciarEdicao(endereco)}
                        disabled={editando !== null}
                    />
                ),
        },
    ];

    return (
        <PageLayout
            title="Endereço Fiscal"
            subtitle="Endereços disponíveis para contratação do serviço de endereço fiscal."
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Endereço Fiscal' }]}
        >
            <DataTable
                columns={columns}
                rows={enderecos}
                getRowKey={(e, i) => e?.idEnderecoFiscal ?? i}
                loading={loading}
                emptyMessage="Nenhum endereço fiscal cadastrado."
            />
        </PageLayout>
    );
}

export default EnderecoFiscal;
